import type { ScrollAxis } from "./scroll-box-axes"
import { MAXIMUM_ELEMENTS_READ_PER_WALK, descendantsNearView, isLongListParent } from "./scroll-box-near-view"
import { rectSpan, scrollportSpan, type PixelSpan } from "./scroll-box-spans"
import {
  confinesOverflowAlong,
  escapesClipOf,
  hasNoDisplay,
  hasOwnText,
  isAbsolutelyPositioned,
  isRendered,
  isStaticallyPositioned,
} from "./scroll-box-styles"

const CONTROL_SELECTOR = [
  "a[href]",
  "button",
  'input:not([type="hidden"])',
  "select",
  "textarea",
  '[role="button"]',
  '[role="link"]',
  '[role="checkbox"]',
  '[role="switch"]',
  '[role="tab"]',
  '[role="menuitem"]',
  '[role="option"]',
  '[contenteditable="true"]',
].join(",")
const HIDDEN_FROM_USERS_SELECTOR = '[aria-hidden="true"], [inert]'
const VISUALLY_HIDDEN_MAXIMUM_PX = 1
const FULLY_TRANSPARENT_OPACITY = "0"

export interface ContentWithin {
  leaves: Element[]
  innerBoxes: Element[]
}

export function isControl(element: Element): boolean {
  return element.matches(CONTROL_SELECTOR)
}

function isFullyTransparent(element: Element): boolean {
  for (let ancestor: Element | null = element; ancestor; ancestor = ancestor.parentElement) {
    if (getComputedStyle(ancestor).opacity === FULLY_TRANSPARENT_OPACITY) return true
  }
  return false
}

export function isShownToUsers(element: Element): boolean {
  if (!isRendered(element)) return false
  const rect = element.getBoundingClientRect()
  if (rect.width <= VISUALLY_HIDDEN_MAXIMUM_PX || rect.height <= VISUALLY_HIDDEN_MAXIMUM_PX) return false
  if (getComputedStyle(element).visibility !== "visible") return false
  return element.closest(HIDDEN_FROM_USERS_SELECTOR) === null && !isFullyTransparent(element)
}

export function isContentLeaf(element: Element): boolean {
  return (isControl(element) || hasOwnText(element)) && isShownToUsers(element)
}

export function ownTextRects(element: Element): DOMRect[] {
  const range = document.createRange()
  return Array.from(element.childNodes)
    .filter((node) => node instanceof Text && node.data.trim().length > 0)
    .flatMap((node) => {
      range.selectNodeContents(node)
      return Array.from(range.getClientRects())
    })
    .filter((rect) => rect.width > 0 && rect.height > 0)
}

export function contentSpans(leaf: Element, axis: ScrollAxis): PixelSpan[] {
  if (isControl(leaf)) return [rectSpan(leaf.getBoundingClientRect(), axis)]
  return ownTextRects(leaf).map((rect) => rectSpan(rect, axis))
}

export function contentRects(leaf: Element): DOMRect[] {
  return isControl(leaf) ? [leaf.getBoundingClientRect()] : ownTextRects(leaf)
}

export function innerBoxSpan(innerBox: Element, axis: ScrollAxis): PixelSpan {
  return scrollportSpan(innerBox, axis)
}

function positionedDescendantsEscaping(box: Element): Element[] {
  if (!isStaticallyPositioned(box)) return []
  const escaping = descendantsNearView(box).filter(
    (descendant) => isAbsolutelyPositioned(descendant) && escapesClipOf(descendant, box),
  )
  return escaping.filter((descendant) => !escaping.some((other) => other !== descendant && other.contains(descendant)))
}

export function collectContentWithin(
  container: Element,
  axis: ScrollAxis,
  escapesContainer: (element: Element, container: Element) => boolean,
  holdsNoContentSought: (element: Element) => boolean = () => false,
): ContentWithin {
  const content: ContentWithin = { leaves: [], innerBoxes: [] }
  let elementsRead = 0
  const visit = (element: Element) => {
    if (elementsRead > MAXIMUM_ELEMENTS_READ_PER_WALK) return
    if (hasNoDisplay(element) || escapesContainer(element, container)) return
    if (confinesOverflowAlong(element, axis) && isRendered(element)) {
      if (isShownToUsers(element)) content.innerBoxes.push(element)
      return
    }
    if (holdsNoContentSought(element)) {
      positionedDescendantsEscaping(element).forEach(visit)
      return
    }
    elementsRead++
    if (isContentLeaf(element)) content.leaves.push(element)
    visitChildrenOf(element)
  }
  const visitChildrenOf = (parent: Element) => {
    const children = Array.from(parent.children)
    const measured = isLongListParent(parent) ? children.filter((child) => !holdsNoContentSought(child)) : children
    measured.forEach(visit)
  }
  visitChildrenOf(container)
  return elementsRead > MAXIMUM_ELEMENTS_READ_PER_WALK ? { leaves: [], innerBoxes: [] } : content
}

export function containsContentLeaf(element: Element): boolean {
  return isContentLeaf(element) || Array.from(element.querySelectorAll("*")).some(isContentLeaf)
}
